import { useEffect, useMemo, useState } from "react";
import mammoth from "mammoth";
import { FileAnalysisResult } from "./FileAnalyser";
import { generateTemplateImage } from "../ImageGeneration/generateTemplateImage";
import { resolveTemplate } from "./templateMapping";
import { templates } from "../ImageGeneration/templates";
import { ContentGenerator, PostType } from "../TextGeneration/contentGenerator";
import { useAuth } from "../Authentification/AuthContext";
import { createAuthFetch } from "../Authentification/apiFetch";
import { createPostPlanApi } from "./PostPlanAPI";
import { PostPlan, PostPlanStatus, PostEntryData } from "./PostPlanTypes";
import { GuideStep, fetchGuideSteps, guideImageUrl } from "./guideApi";
import PostPlanEntryCard from "./PostPlanEntryCard";
import PostImplementationGuide from "./PostImplementationGuide";
import { USECASE_LABELS } from "./usecaseConfig";
import { downloadPostPlan } from "./downloadPostPlan";
import { loadImageElement, analyzeImageProperties, Orientation, Brightness } from "./imageProperties"
import * as pdfjsLib from "pdfjs-dist";
import * as XLSX from "xlsx";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PostPlanBuilderProps {
    analysedFiles: FileAnalysisResult[];
    files: File[];
    language: string;
    usecases: string[];
    initialPlan?: PostPlan | null; // NEU
    onGeneratingChange?: (isGenerating: boolean, step: string) => void;
    onGenerationDuration?: (durationMs: number, generatedPostCount: number) => void;
}

export interface PostEntry {
  id: string;
  fileName: string;
  linkedFileName: string | null;
  baseName: string;
  postType: PostType | null;
  postPotential: FileAnalysisResult["postPotential"] | null;
  reasoning: string;
  usecase: string | null;
  imageDataUrl: string | null;
  imageError?: string;
  text: string;
  textError?: string;
  scheduledDate: Date;
  selected: boolean;
}

interface PostGenerationTiming {
  fileName: string;
  postType: PostType | null;
  durationMs: number;
  generated: boolean;
}

const VALID_POST_TYPES: PostType[] = [
    "speaker_announcement", "exponat_announcement", "event_announcement",
    "event_attendance_announcement", "event_review", "short_post", "first_spirit", "first_spirit_referenten"
];
const isValidPostType = (value: string | undefined): value is PostType =>
    !!value && VALID_POST_TYPES.includes(value as PostType);

const TEXT_MIME_TYPES = [
    "text/plain", "text/markdown", "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/rtf", "text/rtf", "application/pdf", "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const TEXT_EXTENSIONS = /\.(txt|md|csv|doc|docx|rtf|pdf|xls|xlsx)$/i;

const isTextFile = (file: File): boolean =>
    TEXT_MIME_TYPES.includes(file.type) ||
    TEXT_EXTENSIONS.test(file.name);

const readTextFromPdf = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({
    data: arrayBuffer,
  }).promise;

  const pages: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();

    const text = content.items
      .map((item: any) => ("str" in item ? item.str : ""))
      .join(" ");

    pages.push(text);
  }

  return pages.join("\n\n");
};

const readTextFromFile = async (file: File): Promise<string> => {
  const extension = file.name
    .toLowerCase()
    .split(".")
    .pop();

  // DOCX
  if (
    file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    extension === "docx"
  ) {
    const arrayBuffer = await file.arrayBuffer();

    const result = await mammoth.extractRawText({
      arrayBuffer,
    });

    return result.value;
  }

  // PDF
  if (
    file.type === "application/pdf" ||
    extension === "pdf"
  ) {
    return await readTextFromPdf(file);
  }

  // XLSX
  if (
    file.type === "application/vnd.ms-excel" ||
    file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    extension === "xls" ||
    extension === "xlsx"
    ) {
    const excelText = await readTextFromExcel(file);

        console.log("EXCEL TEXT:", {
        fileName: file.name,
        length: excelText.length,
        preview: excelText.slice(0, 1000),
        });

        return excelText;
    }

  // TXT, Markdown, CSV
  if (
    file.type === "text/plain" ||
    file.type === "text/markdown" ||
    file.type === "text/csv" ||
    ["txt", "md", "csv"].includes(extension ?? "")
  ) {
    return await file.text();
  }

  throw new Error(
    `Dateityp "${extension ?? file.type}" kann nicht als Text gelesen werden.`
  );
};

const readTextFromExcel = async (
  file: File
): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();

  const workbook = XLSX.read(arrayBuffer, {
    type: "array",
  });

  const sheets = workbook.SheetNames.map((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];

    const csv = XLSX.utils.sheet_to_csv(worksheet);

    return `Tabelle: ${sheetName}\n${csv}`;
  });

  return sheets.join("\n\n");
};

const filenameOverlapScore = (a: string, b: string): number => {
    const normalize = (s: string) => s.toLowerCase().replace(/\.[^.]+$/, "");
    const strA = normalize(a);
    const strB = normalize(b);
    let maxLen = 0;
    for (let i = 0; i < strA.length; i++) {
        for (let j = 0; j < strB.length; j++) {
            let len = 0;
            while (i + len < strA.length && j + len < strB.length && strA[i + len] === strB[j + len]) len++;
            maxLen = Math.max(maxLen, len);
        }
    }
    return maxLen;
};

interface MatchedText { text: string; sourceFileName: string | null; }

const findMatchingText = async (
    imageFileName: string, postType: string | undefined, files: File[], analysedFiles: FileAnalysisResult[]
): Promise<MatchedText> => {
    const textCandidates = files.filter((f) => {
        if (!isTextFile(f)) return false;

        return analysedFiles.some(
            (a) =>
                a.fileName === f.name &&
                a.suggestedPostTypes[0] === postType
        );
    });
    if (textCandidates.length === 0) return { text: "", sourceFileName: null };

    let bestFile = textCandidates[0];
    if (textCandidates.length > 1) {
        let bestScore = -1;
        for (const candidate of textCandidates) {
            const score = filenameOverlapScore(imageFileName, candidate.name);
            if (score > bestScore) { bestScore = score; bestFile = candidate; }
        }
    }
    return { text: await readTextFromFile(bestFile), sourceFileName: bestFile.name };
};

const CTA_BY_POST_TYPE: Record<PostType, string> = {
    speaker_announcement: "Neugierig geworden? Das ganze Programm findet Ihr auf unserer Webseite: https://www.etit.tu-darmstadt.de/serious-games/eventmedia_sg/gamedays_sg/index.de.jsp",
    exponat_announcement: "Ihr wollt dabei sein? Dann sichert euch jetzt euren Platz. Hier geht er zur Anmeldung: https://www.etit.tu-darmstadt.de/serious-games/eventmedia_sg/gamedays_sg/gamedays_anmeldung_sg.de.jsp",
    event_announcement: "Mehr Informationen findet Ihr auf unserer Webseite: [Link]",
    event_attendance_announcement: "Weitere Infos gibt es auf unserer Webseite: [Link]",
    event_review: "Ihr wollt beim nächsten Event dabei sein? Folgt uns um kein Update mehr zu verpassen!",
    short_post: "Mehr Infos zu unserer Arbeitsgruppe findet ihr auf unserer Webseite: https://www.etit.tu-darmstadt.de/serious-games/willkommen_sg/index.de.jsp",
    first_spirit: "",
    first_spirit_referenten: "",
};

const formatDateTimeGerman = (date: Date): string =>
    date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" }) +
    ", " + date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";

// Immer donnerstags, 9-12 Uhr
const getNextPostDays = (count: number): Date[] => {
    const result: Date[] = [];
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() + 1);

    const THURSDAY = 4;
    while (current.getDay() !== THURSDAY) current.setDate(current.getDate() + 1);

    while (result.length < count) {
        const postDate = new Date(current);
        const randomMinutes = Math.floor(Math.random() * (3 * 60));
        postDate.setHours(9, randomMinutes, 0, 0);
        result.push(postDate);
        current.setDate(current.getDate() + 7);
    }
    return result;
};

const contentGenerator = new ContentGenerator();

const LINKEDIN_USECASES = ["LinkedInAG", "LinkedInGameDays", "LinkedInPersonal"];
const FIRSTSPIRIT_USECASES = ["FirstSpiritAG", "FirstSpiritTU"];

export default function PostPlanBuilder({ analysedFiles, files, language, usecases, initialPlan, onGeneratingChange, onGenerationDuration }: PostPlanBuilderProps) {
    const { user, token, hasRole } = useAuth();
    const api = useMemo(() => createPostPlanApi(token), [token]);
    const apiFetch = useMemo(() => createAuthFetch(token), [token]);

    const [entries, setEntries] = useState<PostEntry[]>([]);
    const [postIndex, setPostIndex] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStep, setGenerationStep] = useState("");
    const [generationProgress, setGenerationProgress] = useState(0);

    const [planId, setPlanId] = useState<string | null>(null);
    const [planStatus, setPlanStatus] = useState<PostPlanStatus | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [guidesByPost, setGuidesByPost] = useState<Record<string, GuideStep[]>>({})
    const [selectedGuideUsecase, setSelectedGuideUsecase] = useState<Record<string, string>>({}); // entry.id -> usecase
    const LINKEDIN_USECASES = ["LinkedInAG", "LinkedInGameDays", "LinkedInPersonal"];
    const FIRSTSPIRIT_USECASES = ["FirstSpiritAG", "FirstSpiritTU"];
    const [guideExpanded, setGuideExpanded] = useState<Record<string, boolean>>({}); // entry.id -> ausgeklappt?
    const [guideStepIndex, setGuideStepIndex] = useState<Record<string, number>>({}); // entry.id -> aktueller Schritt
    const FIRSTSPIRIT_POST_TYPES: PostType[] = ["first_spirit", "first_spirit_referenten"];

    const resolveUsecasesForEntry = (
        entry: PostEntry,
        planUsecases: string[]
        ): string[] => {
        if (!entry.postType) return [];

        const relevantPool = FIRSTSPIRIT_POST_TYPES.includes(entry.postType)
            ? FIRSTSPIRIT_USECASES
            : LINKEDIN_USECASES;

        // Individuell am Post gesetzter Usecase hat Vorrang
        if (
            entry.usecase &&
            relevantPool.includes(entry.usecase)
        ) {
            return [entry.usecase];
        }

        // Fallback für ältere Postpläne
        return planUsecases.filter((usecase) =>
            relevantPool.includes(usecase)
        );
    };

    const resolveUsecasesForPostType = (
        postType: PostType | null | undefined,
        usecases: string[]
        ): string[] => {
        if (!postType) return [];

        const relevantPool = FIRSTSPIRIT_POST_TYPES.includes(postType)
            ? FIRSTSPIRIT_USECASES
            : LINKEDIN_USECASES;

        return usecases.filter((usecase) =>
            relevantPool.includes(usecase)
        );
    };

    const resolveUsecaseForPostType = (
        postType: PostType | null | undefined,
        usecases: string[]
        ): string | null => {
        return resolveUsecasesForPostType(postType, usecases)[0] ?? null;
    };

    const resolvePostType = (
        suggestedPostTypes: PostType[],
        suggestedImageTemplates: string[],
        usecases: string[]
        ): PostType | undefined => {
        if (suggestedPostTypes.length > 0) {
            return suggestedPostTypes[0];
        }

        const hasFirstSpiritUsecase = usecases.some((usecase) =>
            FIRSTSPIRIT_USECASES.includes(usecase)
        );

        if (!hasFirstSpiritUsecase) {
            return undefined;
        }

        const isSpeakerTemplate = suggestedImageTemplates.some((template) =>
            template.toLowerCase().includes("referent")
        );

        return isSpeakerTemplate
            ? "first_spirit_referenten"
            : "first_spirit";
    };

    function resolveShortPostBranding(usecases: string[]): "GameDays" | "AG" {
        return usecases.includes("LinkedInGameDays") ? "GameDays" : "AG";
        }

        async function resolveShortPostTemplateName(file: File, usecases: string[]): Promise<string | null> {
        try {
            const img = await loadImageElement(file);
            const { orientation, brightness } = analyzeImageProperties(img);
            const branding = resolveShortPostBranding(usecases);
            const orientationLabel = orientation === "landscape" ? "Landscape" : "Portrait";
            const brightnessLabel = brightness === "light" ? "Light" : "Dark";
            return `${branding} ${orientationLabel} Format ${brightnessLabel}`;
        } catch (err) {
            console.error("Short-Post-Template konnte nicht ermittelt werden:", err);
            return null;
        }
    }

    useEffect(() => {
        onGeneratingChange?.(isGenerating, generationStep);
    }, [isGenerating, generationStep, onGeneratingChange]);

    useEffect(() => {
        if (!initialPlan) return;
        setEntries(initialPlan.entries.map(fromEntryData));
        setPostIndex(0);
        setPlanId(initialPlan.id);
        setPlanStatus(initialPlan.status);
        setActionError(null);
    }, [initialPlan]);

    useEffect(() => {
        if (entries.length === 0) return;
        let cancelled = false;

        const loadGuides = async () => {
            const uniquePairs = new Map<string, { usecase: string; postType: string }>();
            for (const entry of entries) {
                if (!entry.postType) continue;
                const matchedUsecases = resolveUsecasesForEntry(entry, usecases);
                for (const usecase of matchedUsecases) {
                    uniquePairs.set(`${usecase}::${entry.postType}`, { usecase, postType: entry.postType });
                }
            }

            const results = await Promise.all(
                Array.from(uniquePairs.entries()).map(async ([key, { usecase, postType }]) => {
                    const steps = await fetchGuideSteps(usecase, postType);
                    return [key, steps] as const;
                })
            );

            if (cancelled) return;
            setGuidesByPost(Object.fromEntries(results));
            setGuideStepIndex({});
        };

        void loadGuides();
        return () => { cancelled = true; };
    }, [entries, usecases]);

    // Generierung: baut direkt PostEntry[] statt getrennter Bild-/Text-Arrays
    useEffect(() => {
        if (analysedFiles.length === 0) return;
        let cancelled = false;

        const run = async () => {
            const generationStartedAt = performance.now();

            const postTimings: PostGenerationTiming[] = [];


            setIsGenerating(true);
            setGenerationProgress(0);
            setEntries([]);
            setPostIndex(0);
            setPlanId(null);
            setPlanStatus(null);

            const rawEntries: Omit<PostEntry, "scheduledDate">[] = [];
            const consumedTextFileNames = new Set<string>();
            const getChannelKey = (postType: PostType | null | undefined) =>
                postType === "first_spirit" ||
                postType === "first_spirit_referenten"
                    ? "website"
                    : "linkedin";

            const orderedAnalysedFiles = [...analysedFiles].sort((a, b) => {
                const fileA = files.find((f) => f.name === a.fileName);
                const fileB = files.find((f) => f.name === b.fileName);

                const aIsImage = fileA?.type.startsWith("image/") ?? false;
                const bIsImage = fileB?.type.startsWith("image/") ?? false;

                if (aIsImage === bIsImage) return 0;

                return aIsImage ? -1 : 1;
            });

            for (let i = 0; i < orderedAnalysedFiles.length; i++) {
                const postStartedAt = performance.now();
                const entriesBefore = rawEntries.length;

                const analysed = orderedAnalysedFiles[i];
                setGenerationStep(`Verarbeite ${analysed.fileName} (${i + 1}/${orderedAnalysedFiles.length})...`);
                setGenerationProgress(Math.round(((i + 1) / orderedAnalysedFiles.length) * 100));

                const originalFile = files.find((f) => f.name === analysed.fileName) ?? null;
                if (!originalFile) continue;

                const postType = resolvePostType(
                    analysed.suggestedPostTypes,
                    analysed.suggestedImageTemplates,
                    usecases
                );

                const baseName = analysed.fileName.replace(/\.[^.]+$/, "");
                const fileIsImage = originalFile.type.startsWith("image/");
                const fileIsText = isTextFile(originalFile);

                if (fileIsImage) {
                    const matchingText = await findMatchingText(analysed.fileName, postType, files, analysedFiles);
                    if (matchingText.sourceFileName) {
                        consumedTextFileNames.add(
                            `${matchingText.sourceFileName}::${getChannelKey(postType)}`
                        );
                    }

                    let template = analysed.suggestedImageTemplates[0]
                        ? templates.find((t) => t.name === analysed.suggestedImageTemplates[0]) ?? null
                        : (postType ? resolveTemplate(postType, templates) : null);

                    if (!template && postType === "short_post") {
                        const shortPostTemplateName = await resolveShortPostTemplateName(originalFile, usecases);
                        if (shortPostTemplateName) {
                            template = templates.find((t) => t.name === shortPostTemplateName) ?? null;
                        }
                    }

                    let imageDataUrl: string | null = null;
                    let imageError: string | undefined;
                    if (template) {
                        try {
                            imageDataUrl = await generateTemplateImage(originalFile, matchingText.text, template, apiFetch);
                        } catch (err) {
                            console.error(`Bild für ${analysed.fileName} konnte nicht generiert werden:`, err);
                            imageError = "Bild konnte nicht generiert werden.";
                        }
                    } else {
                        imageError = analysed.suggestedImageTemplates[0]
                            ? `Template "${analysed.suggestedImageTemplates[0]}" nicht gefunden.`
                            : (postType ? `Kein Template für "${postType}" gefunden.` : "Kein Post-Type ermittelt.");
                    }

                    // IMMER durch die KI generieren lassen - egal ob matchingText.text
                    // leer ist (dann generiert die KI frei) oder Inhalt hat (dann nutzt
                    // sie ihn als Grundlage für den Post-Text).
                    let text = "";
                    let textError: string | undefined;
                    if (isValidPostType(postType)) {
                        try {
                            text = await contentGenerator.generatePost(matchingText.text, postType, language, apiFetch);
                        } catch (err) {
                            console.error(`Text für ${analysed.fileName} konnte nicht generiert werden:`, err);
                            textError = "Text konnte nicht generiert werden.";
                        }
                    }

                    rawEntries.push({
                        id: crypto.randomUUID(),
                        fileName: analysed.fileName,
                        linkedFileName: matchingText.sourceFileName,
                        baseName,
                        postType: postType ?? null,
                        postPotential: analysed.postPotential,
                        reasoning: analysed.reasoning,
                        usecase: resolveUsecaseForPostType(postType, usecases),
                        imageDataUrl,
                        imageError,
                        text,
                        textError,
                        selected: true,
                    });
                } else if (fileIsText) {
                    if (
                        consumedTextFileNames.has(
                            `${analysed.fileName}::${getChannelKey(postType)}`
                        )
                    ) {
                        continue;
                    }
                    if (!isValidPostType(postType)) continue;

                    let text = "";
                    let textError: string | undefined;
                    try {
                        const freeText = await readTextFromFile(originalFile);
                        text = await contentGenerator.generatePost(freeText, postType, language, apiFetch);
                    } catch (err) {
                        console.error(`Text für ${analysed.fileName} konnte nicht generiert werden:`, err);
                        textError = "Text konnte nicht generiert werden.";
                    }

                    rawEntries.push({
                        id: crypto.randomUUID(),
                        fileName: analysed.fileName,
                        linkedFileName: null,
                        baseName,
                        postType: postType ?? null,
                        postPotential: analysed.postPotential,
                        reasoning: analysed.reasoning,
                        usecase: resolveUsecaseForPostType(postType, usecases),
                        imageDataUrl: null, text, textError,
                        selected: true,
                    });
                }

                const postDurationMs = performance.now() - postStartedAt;
                const generated = rawEntries.length > entriesBefore;

                postTimings.push({
                    fileName: analysed.fileName,
                    postType: postType ?? null,
                    durationMs: postDurationMs,
                    generated,
                });

                console.log("EVALUATION POST", {
                    fileName: analysed.fileName,
                    postType,
                    durationMs: Math.round(postDurationMs),
                    generated,
                });

            }

            if (cancelled) return;

            const potentialOrder: Record<string, number> = { high: 0, medium: 1, low: 2, none: 3 };
            rawEntries.sort((a, b) => (potentialOrder[a.postPotential ?? "none"] ?? 3) - (potentialOrder[b.postPotential ?? "none"] ?? 3));

            const dates = getNextPostDays(rawEntries.length);
            const scheduled = rawEntries.map((e, idx) => ({ ...e, scheduledDate: dates[idx] }));

            setEntries(scheduled);

            const generationDurationMs =
            performance.now() - generationStartedAt;

            const generatedPostCount = scheduled.length;

            console.log("=== EVALUATION GENERATION ===");

            console.log({
            generationDurationMs:
                Math.round(generationDurationMs),

            generatedPostCount,

            postTimings: postTimings.map((timing) => ({
                ...timing,
                durationMs: Math.round(timing.durationMs),
            })),
            });

            console.table(
            postTimings.map((timing) => ({
                fileName: timing.fileName,
                postType: timing.postType,
                durationMs: Math.round(timing.durationMs),
                generated: timing.generated,
            }))
            );

            onGenerationDuration?.(
            generationDurationMs,
            generatedPostCount
            );

            setIsGenerating(false);
            setGenerationStep("");
            setGenerationProgress(100);
        };

        void run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysedFiles]);

    const goPrev = () => setPostIndex((i) => Math.max(0, i - 1));
    const goNext = () => setPostIndex((i) => Math.min(entries.length - 1, i + 1));

    const updateEntry = (patch: Partial<PostEntry>) => {
        setEntries((prev) => prev.map((e, idx) => (idx === postIndex ? { ...e, ...patch } : e)));
    };

    const currentEntry = entries[postIndex];
    const selectedCount = entries.filter((e) => e.selected).length;

    // --- Speichern / Workflow ---

    const toEntryData = (e: PostEntry): PostEntryData => ({
        id: e.id,
        fileName: e.fileName,
        linkedFileName: e.linkedFileName,
        baseName: e.baseName,
        postType: e.postType,
        postPotential: e.postPotential,
        reasoning: e.reasoning,
        usecase: e.usecase,
        imageDataUrl: e.imageDataUrl,
        imageError: e.imageError,
        text: e.text,
        textError: e.textError,
        scheduledDate: e.scheduledDate.toISOString(),
    });

    const fromEntryData = (e: PostEntryData): PostEntry => ({
        id: e.id,
        fileName: e.fileName,
        linkedFileName: e.linkedFileName,
        baseName: e.baseName,
        postType: e.postType,
        postPotential: e.postPotential,
        reasoning: e.reasoning,
        usecase: e.usecase ?? null,
        imageDataUrl: e.imageDataUrl,
        imageError: e.imageError,
        text: e.text,
        textError: e.textError,
        scheduledDate: new Date(e.scheduledDate),
        selected: true,
    });

    const runAction = async (action: () => Promise<void>) => {
        setIsSaving(true);
        setActionError(null);
        try {
            await action();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Aktion fehlgeschlagen");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => runAction(async () => {
        const payload = { usecases, entries: entries.filter((e) => e.selected).map(toEntryData) };
        const plan = planId ? await api.update(planId, payload) : await api.create(payload);
        setPlanId(plan.id);
        setPlanStatus(plan.status);
    });

    const handleUnpost = () =>
        runAction(async () => {
            if (!planId) return;

            const plan = await api.unpost(planId);
            setPlanStatus(plan.status);
        });

    const handleDownload = async () => {
        try {
            await downloadPostPlan({
            entries,
            analysedFiles,
            });
        } catch (err) {
            console.error(
            "Postplan konnte nicht heruntergeladen werden:",
            err
            );

            setActionError(
            "Postplan konnte nicht heruntergeladen werden."
            );
        }
    };

    const handleSubmitForReview = () => runAction(async () => {
        if (!planId) return;
        const plan = await api.submitForReview(planId);
        setPlanStatus(plan.status);
    });

    const handleConfirm = () => runAction(async () => {
        if (!planId) return;
        const plan = await api.confirm(planId);
        setPlanStatus(plan.status);
    });

    const handleMarkPosted = () => runAction(async () => {
        if (!planId) return;
        const plan = await api.markPosted(planId);
        setPlanStatus(plan.status);
    });

    const handleDelete = () => runAction(async () => {
        if (!planId) return;
        await api.remove(planId);
        setPlanId(null);
        setPlanStatus(null);
    });

    const matchedUsecases = currentEntry?.postType
    ? resolveUsecasesForEntry(currentEntry, usecases)
    : [];

    const activeUsecase =
        currentEntry && matchedUsecases.length > 0
            ? (
                currentEntry.usecase &&
                matchedUsecases.includes(currentEntry.usecase)
                ? currentEntry.usecase
                : selectedGuideUsecase[currentEntry.id] &&
                    matchedUsecases.includes(
                    selectedGuideUsecase[currentEntry.id]
                    )
                ? selectedGuideUsecase[currentEntry.id]
                : matchedUsecases[0]
            )
            : null;

    const guideSteps =
    currentEntry?.postType && activeUsecase
        ? guidesByPost[
            `${activeUsecase}::${currentEntry.postType}`
        ] ?? []
        : [];

    const isAuthor = hasRole("author", "admin");
    const isModerator = hasRole("moderator", "admin");

    return (
    <div className="post-plan-builder">
        {isGenerating && (
            <div className="planner-generation">
                <div className="planner-generation__header">
                <div>
                    <strong>Postplan wird erstellt</strong>
                    <p>
                    {generationStep || "Daten werden verarbeitet..."}
                    </p>
                </div>

                <span className="planner-generation__percent">
                    {generationProgress}%
                </span>
                </div>

                <div className="planner-generation__track">
                <div
                    className="planner-generation__progress"
                    style={{ width: `${generationProgress}%` }}
                />
                </div>
            </div>
        )}

        {entries.length > 0 && (
            <div className="post-plan-summary">
                <div>
                <h2>Postplan</h2>

                <p>
                    {entries.length} Beiträge · {selectedCount} ausgewählt
                </p>
                </div>

                {planStatus && (
                <span className={`plan-status plan-status--${planStatus}`}>
                    {planStatus === "draft" && "Entwurf"}
                    {planStatus === "in_review" && "In Prüfung"}
                    {planStatus === "confirmed" && "Bestätigt"}
                    {planStatus === "posted" && "Veröffentlicht"}
                </span>
                )}
            </div>
        )}

        {currentEntry && (
        <div className="post-plan-carousel">
            <div className="carousel-header">
                <button
                    type="button"
                    className="carousel-arrow"
                    onClick={goPrev}
                    disabled={postIndex === 0}
                >
                    ‹
                </button>

                <div className="carousel-position">
                    <span className="carousel-position__count">
                        Post {postIndex + 1} von {entries.length}
                    </span>

                    {currentEntry.usecase && (
                        <span className="carousel-position__context">
                        {USECASE_LABELS[currentEntry.usecase] ?? currentEntry.usecase}
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    className="carousel-arrow"
                    onClick={goNext}
                    disabled={postIndex === entries.length - 1}
                >
                    ›
                </button>
            </div>

            <div className="carousel-jump-list">
                {entries.map((entry, index) => (
                    <button
                    key={entry.id}
                    type="button"
                    className={
                        index === postIndex
                        ? "carousel-jump carousel-jump--active"
                        : "carousel-jump"
                    }
                    onClick={() => setPostIndex(index)}
                    title={
                        entry.usecase
                        ? USECASE_LABELS[entry.usecase] ?? entry.usecase
                        : `Post ${index + 1}`
                    }
                    >
                    {index + 1}
                    </button>
                ))}
            </div>

            <PostPlanEntryCard
                entry={currentEntry}
                index={postIndex}
                onSelectedChange={(selected) =>
                    updateEntry({ selected })
                }
                onTextChange={(text) =>
                    updateEntry({ text })
                }
                onScheduledDateChange={(scheduledDate) =>
                    updateEntry({ scheduledDate })
                }
                onUsecaseChange={(usecase) =>
                    updateEntry({ usecase })
                }
                ctaText={
                    currentEntry.postType
                    ? CTA_BY_POST_TYPE[currentEntry.postType]
                    : undefined
                }
            >
            <PostImplementationGuide
                entry={currentEntry}
                matchedUsecases={matchedUsecases}
                activeUsecase={activeUsecase}
                guideSteps={guideSteps}
                expanded={guideExpanded[currentEntry.id] ?? false}
                currentStepIndex={guideStepIndex[currentEntry.id] ?? 0}
                onToggle={() =>
                setGuideExpanded((prev) => ({
                    ...prev,
                    [currentEntry.id]: !prev[currentEntry.id],
                }))
                }
                onUsecaseChange={(usecase) => {
                setSelectedGuideUsecase((prev) => ({
                    ...prev,
                    [currentEntry.id]: usecase,
                }));

                setGuideStepIndex((prev) => ({
                    ...prev,
                    [currentEntry.id]: 0,
                }));
                }}
                onStepChange={(step) =>
                setGuideStepIndex((prev) => ({
                    ...prev,
                    [currentEntry.id]: step,
                }))
                }
                formatDate={formatDateTimeGerman}
                usecaseLabels={USECASE_LABELS}
                guideImageUrl={guideImageUrl}
            />
            </PostPlanEntryCard>
        </div>
        )}

        {entries.length > 0 && (
            <div className="post-plan-actions">
  {actionError && (
    <p className="post-plan-error">
      {actionError}
    </p>
  )}

  {planStatus && (
    <p className="post-plan-status">
      Status: {planStatus}
      {planStatus === "in_review"
        ? " (wartet auf Prüfung)"
        : ""}
    </p>
  )}

  <div className="post-plan-actions__row">
        <div className="post-plan-actions__status">
            {planStatus && (
            <span className={`plan-status plan-status--${planStatus}`}>
                {planStatus === "draft" && "Entwurf"}
                {planStatus === "in_review" && "In Prüfung"}
                {planStatus === "confirmed" && "Bestätigt"}
                {planStatus === "posted" && "Veröffentlicht"}
            </span>
            )}
        </div>
        <div className="post-plan-actions__main">
            {(isAuthor || isModerator) &&
                (!planStatus || planStatus === "draft") && (
                <button
                    className="btn-secondary"
                    onClick={handleSave}
                    disabled={isSaving || selectedCount === 0}
                >
                    {isSaving
                    ? "Speichert..."
                    : planId
                    ? "Änderungen speichern"
                    : `Postplan speichern (${selectedCount})`}
                </button>
                )}

            {isAuthor &&
                planId &&
                planStatus === "draft" && (
                <button
                    className="btn-primary"
                    onClick={handleSubmitForReview}
                    disabled={isSaving}
                >
                    Zur Prüfung einreichen
                </button>
                )}

            {isModerator &&
                planId &&
                planStatus === "in_review" && (
                <>
                    <button
                    className="btn-secondary"
                    onClick={handleSave}
                    disabled={isSaving}
                    >
                    Änderungen speichern
                    </button>

                    <button
                    className="btn-primary"
                    onClick={handleConfirm}
                    disabled={isSaving}
                    >
                    Bestätigen
                    </button>
                </>
                )}

           {(isAuthor || isModerator) &&
                planId &&
                planStatus === "posted" && (
                    <>
                    <button
                        className="btn-secondary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Änderungen speichern
                    </button>

                    <button
                        className="btn-secondary"
                        onClick={handleUnpost}
                        disabled={isSaving}
                    >
                        Als nicht gepostet markieren
                    </button>
                    </>
            )}

            {(isAuthor || isModerator) &&
                planId &&
                planStatus === "confirmed" && (
                <button
                    className="btn-primary"
                    onClick={handleMarkPosted}
                    disabled={isSaving}
                >
                    Als gepostet markieren
                </button>
                )}
            </div>

            <button
                type="button"
                className="btn-secondary"
                onClick={handleDownload}
                disabled={selectedCount === 0}
            >
                ZIP herunterladen
            </button>

            <div className="post-plan-actions__danger">
            {(isAuthor || isModerator) && planId && (
                <button
                className="btn-danger"
                onClick={handleDelete}
                disabled={isSaving}
                >
                Postplan löschen
                </button>
            )}
            </div>
        </div>
    </div>
        )}
    </div>
);
}